import { API, graphqlOperation } from 'aws-amplify'
import { metricDataByClientDate } from 'graphql/queries'
import { parseISO, differenceInDays, addDays, format } from 'date-fns'

function createDateFilter(startDate, endDate) {
  return startDate === endDate
    ? { eq: startDate }
    : { between: [startDate, endDate] }
}

function createMetricFilter(metricNames) {
  return {
    or: Object.values(metricNames).map(metricName => ({
      metricName: { eq: metricName }
    }))
  }
}

function calculateDayCount(startDate, endDate) {
  // Use parseISO to avoid timezone issues
  const start = parseISO(startDate);
  const end = parseISO(endDate);
  return differenceInDays(end, start) + 1;
}

async function fetchMetricDataByClientDateAndName(
  clientId,
  startDate,
  endDate,
  metricNames,
  batchSize = 1000,
) {

  const dateFilter = createDateFilter(startDate, endDate);
  const metricFilter = createMetricFilter(metricNames);

  console.log('🔍 MetricData Query Debug:', {
    clientId,
    startDate,
    endDate,
    expectedDayCount: calculateDayCount(startDate, endDate),
    dateFilter,
    metricFilter,
    batchSize
  });

  const variables = {
    clientId: clientId,
    date: dateFilter,
    filter: metricFilter,
    limit: batchSize
  }

  let allData = []
  let nextToken = null
  let batchCount = 0

  const metricTracker = new Map()
  Object.values(metricNames).forEach(metric => metricTracker.set(metric, 0))

  try {
    do {
      batchCount++
      // console.log(`Fetching batch ${batchCount}`)

      console.log(`📡 Batch ${batchCount} GraphQL query:`, {
        query: 'metricDataByClientDate',
        variables: {
          clientId: variables.clientId,
          date: variables.date,
          limit: variables.limit,
          hasNextToken: !!nextToken
        }
      });

      const response = await API.graphql(graphqlOperation(metricDataByClientDate, {
        ...variables,
        nextToken
      }))

      const { items, nextToken: newNextToken } = response.data.metricDataByClientDate
      
      console.log(`📦 Batch ${batchCount} response:`, {
        itemCount: items ? items.length : 0,
        hasNextToken: !!newNextToken,
        sampleDates: items && items.length > 0 ? 
          [...new Set(items.slice(0, 10).map(item => item.date))].sort() : [],
        sampleStores: items && items.length > 0 ?
          [...new Set(items.slice(0, 10).map(item => item.storeId.slice(-8)))].slice(0, 3) : []
      });

      console.log('items', items)

      items.forEach(item => {
        metricTracker.set(item.metricName, (metricTracker.get(item.metricName) || 0) + 1)
      })

      // Log batch details
      // const batchMetrics = Object.groupBy(items, item => item.metricName)

      // console.log('batchMetrics', batchMetrics)

      // console.log('Batch Analysis:', {
      //   batchNumber: batchCount,
      //   totalItems: items.length,
      //   metricsPresent: Object.keys(batchMetrics),
      //   itemsPerMetric: Object.fromEntries(
      //     Object.entries(batchMetrics).map(([metric, items]) => [metric, items.length])
      //   ),
      //   hasMoreData: !!newNextToken
      // })

      allData = allData.concat(items)
      nextToken = newNextToken
    } while (nextToken)
    
    // Analyze returned dates for debugging
    const returnedDates = [...new Set(allData.map(item => item.date))].sort();
    const expectedDayCount = calculateDayCount(startDate, endDate);
    
    console.log('📅 MetricData Date Analysis:', {
      expectedDayCount,
      actualDayCount: returnedDates.length,
      missingDays: expectedDayCount - returnedDates.length,
      returnedDates: returnedDates,
      startDate,
      endDate
    });

    // Check for missing dates in the range using date-fns to avoid timezone issues
    const expectedDates = [];
    const start = parseISO(startDate);
    const end = parseISO(endDate);
    
    let currentDate = start;
    while (currentDate <= end) {
      expectedDates.push(format(currentDate, 'yyyy-MM-dd'));
      currentDate = addDays(currentDate, 1);
    }
    
    const missingDates = expectedDates.filter(date => !returnedDates.includes(date));
    if (missingDates.length > 0) {
      console.warn('⚠️ Missing dates in MetricData:', {
        missingDates,
        missingCount: missingDates.length,
        missingDaysOfWeek: missingDates.map(date => {
          const d = parseISO(date);
          return `${date} (${d.toLocaleDateString('en-US', { weekday: 'long', timeZone: 'UTC' })})`;
        })
      });
    }

    // Also show what dates we DID get with day of week
    console.log('✅ Returned dates with day of week:', 
      returnedDates.slice(0, 10).map(date => {
        const d = parseISO(date);
        return `${date} (${d.toLocaleDateString('en-US', { weekday: 'long', timeZone: 'UTC' })})`;
      })
    );

    // const finalMetricCounts = Object.groupBy(allData, item => item.metricName)
    // console.log('Final fetch summary:', {
    //   totalBatches:  batchCount,
    //   totalItems: allData.length,
    //   itemsPerMetric: Object.fromEntries(
    //     Object.entries(finalMetricCounts).map(([metric, items]) => [metric, items.length])
    //   ),
    //   dateRange: { startDate, endDate }
    // })

    console.log('allData', allData)
    return allData
  } catch (error) {
    console.error('Error in fetchMetricDatabyClientDateAndName', error)
    throw error
  }
}

export { fetchMetricDataByClientDateAndName }